import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CardContent,
  Skeleton,
  Typography,
} from '@mui/material';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import AssignmentAddRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import GlassCard from '../Cards/GlassCard';
import { ctGetRecentActivity } from '../../services/api';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { PRIMARY } from '../../utils/colors';
import { useDashboardShell } from '../../context/DashboardShellContext';

const REFRESH_MS = 30_000;

const TYPE_ICON = {
  PROGRESS_REFRESHED: SyncRoundedIcon,
  ASSIGNMENT_ADDED: AssignmentAddRoundedIcon,
  STUDENT_ACTIVE: CodeRoundedIcon,
  EXPORT_GENERATED: FileDownloadRoundedIcon,
};

const TYPE_ICON_COLOR = {
  PROGRESS_REFRESHED: 'primary.main',
  ASSIGNMENT_ADDED: 'secondary.main',
  STUDENT_ACTIVE: 'success.main',
  EXPORT_GENERATED: 'info.main',
};

const ActivityTimelineItem = ({ event, isLast }) => {
  const Icon = TYPE_ICON[event.type] || HistoryRoundedIcon;
  const iconColor = TYPE_ICON_COLOR[event.type] || 'text.secondary';
  const rel = formatRelativeTime(event.timestamp);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.75,
        position: 'relative',
        pb: isLast ? 0 : 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            zIndex: 1,
          }}
        >
          <Icon sx={{ fontSize: 20, color: iconColor }} />
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              minHeight: 28,
              flex: '0 0 auto',
              mt: 0.5,
              bgcolor: 'divider',
              borderRadius: 1,
            }}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.45 }}>
          {event.message}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {event.batchName}
          {rel ? ` · ${rel}` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const TimelineShimmer = () => (
  <Box sx={{ py: 0.5 }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Box
        key={i}
        sx={{
          display: 'flex',
          gap: 1.75,
          pb: 2,
          alignItems: 'flex-start',
        }}
      >
        <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="rounded" width="92%" height={18} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width="55%" height={14} sx={{ borderRadius: 1, mt: 1 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

/**
 * Recent activity from GET /api/coding-tracker/recent-activity — vertical timeline, auto-refresh.
 */
const RecentActivityCard = ({ autoRefreshMs = REFRESH_MS }) => {
  const shell = useDashboardShell();
  const activityPath = shell?.codingActivityPath ?? '/faculty/coding/activity';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await ctGetRecentActivity();
      setEvents(data?.events ?? []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (autoRefreshMs <= 0) return undefined;
    const id = window.setInterval(() => {
      load();
    }, autoRefreshMs);
    return () => window.clearInterval(id);
  }, [autoRefreshMs, load]);

  return (
    <GlassCard hoverable={false} sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2.5, width: '100%', '&:last-child': { pb: 2.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                color: 'primary.main',
                display: 'flex',
              }}
            >
              <HistoryRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" component="h2" fontWeight={600} noWrap>
              Recent activity
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={activityPath}
            size="small"
            variant="text"
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 600,
              color: PRIMARY,
            }}
          >
            View all
          </Button>
        </Box>

        {loading ? (
          <TimelineShimmer />
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No recent activity
          </Typography>
        ) : (
          <Box sx={{ pl: 0.25 }}>
            {events.map((ev, i) => (
              <ActivityTimelineItem
                key={`${ev.timestamp}-${i}-${ev.type}`}
                event={ev}
                isLast={i === events.length - 1}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
};

export default RecentActivityCard;
